import { useQuery } from '@rocicorp/zero/react';
import type { MaybeQueryResult, UseQueryOptions } from '@rocicorp/zero/react';
import type {
  BaseDefaultContext,
  BaseDefaultSchema,
  DefaultContext,
  DefaultSchema,
  Falsy,
  PullRow,
  QueryOrQueryRequest,
  ReadonlyJSONValue,
} from '@rocicorp/zero';
import type { NormalizedRow } from '@strength/zero-schema';
import { normalizeZeroRow, queryTableName } from '@strength/zero-schema';
import { useIsZeroActive } from '../app/zero/zero-active';

// Always MaybeQueryResult: while the real Zero provider isn't active the query
// is forced falsy and data is undefined, so callers must handle undefined even
// with a real query. Gating on useIsZeroActive (not the raw zeroFlag) is critical:
// the flag can be on before the org session resolves, and running a live query
// against the contextless stub provider crashes (SC-3525).
//
// Outputs are normalized to the tRPC shape for the same row (dates as
// YYYY-MM-DD, timestamps as ISO strings, related rows included) and typed via
// NormalizedRow; query inputs stay epoch-ms numbers — see normalize-zero-row.
export function useZeroQuery<
  TTable extends keyof TSchema['tables'] & string,
  TInput extends ReadonlyJSONValue | undefined,
  TOutput extends ReadonlyJSONValue | undefined,
  TSchema extends BaseDefaultSchema = DefaultSchema,
  TReturn = PullRow<TTable, TSchema>,
  TContext extends BaseDefaultContext = DefaultContext,
>(
  query:
    | QueryOrQueryRequest<TTable, TInput, TOutput, TSchema, TReturn, TContext>
    | Falsy,
  options?: UseQueryOptions | boolean,
): MaybeQueryResult<NormalizedRow<TTable, TReturn>> {
  const isZeroActive = useIsZeroActive();
  const [data, details] = useQuery(isZeroActive ? query : undefined, options);

  const table = queryTableName(query);

  if (data == null) {
    return [undefined, details] as MaybeQueryResult<
      NormalizedRow<TTable, TReturn>
    >;
  }

  if (Array.isArray(data)) {
    // SAFETY: useQuery returns rows of TReturn for this table; the hook's
    // declared output type is the normalized form of the same rows.
    const rows = (data as unknown as readonly TReturn[]).map((row) =>
      normalizeZeroRow(row, table),
    );
    // SAFETY: the runtime rows are normalized to NormalizedRow<TTable, TReturn>,
    // which TS cannot structurally verify against HumanReadable's recursive type.
    return [rows, details] as unknown as MaybeQueryResult<
      NormalizedRow<TTable, TReturn>
    >;
  }

  // SAFETY: single-row (.one()) result of TReturn, same normalization
  // invariant as the array branch above.
  return [
    normalizeZeroRow(data as TReturn, table),
    details,
  ] as unknown as MaybeQueryResult<NormalizedRow<TTable, TReturn>>;
}
